import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../Login/AuthContext';
import Navbar from '../UI/Navbar';
import api from '../../api';
import './PricingPage.css';

// Prix Stripe depuis les variables d'environnement
const STRIPE_PRICE_ID_SUBSCRIPTION = process.env.REACT_APP_STRIPE_PRICE_ID_SUBSCRIPTION;
const STRIPE_PRICE_ID_ONETIME_SMALL = process.env.REACT_APP_STRIPE_PRICE_ID_ONETIME_SMALL;
const STRIPE_PRICE_ID_ONETIME_MEDIUM = process.env.REACT_APP_STRIPE_PRICE_ID_ONETIME_MEDIUM;
const STRIPE_PRICE_ID_ONETIME_LARGE = process.env.REACT_APP_STRIPE_PRICE_ID_ONETIME_LARGE;

const PricingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCredits, setSelectedCredits] = useState('small');

  // Options de crédits pour le paiement ponctuel
  const creditOptions = [
    { id: 'small', name: 'Pack débutant', credits: 50, price: '9,99', priceId: STRIPE_PRICE_ID_ONETIME_SMALL },
    { id: 'medium', name: 'Pack avancé', credits: 150, price: '19,99', priceId: STRIPE_PRICE_ID_ONETIME_MEDIUM },
    { id: 'large', name: 'Pack pro', credits: 500, price: '39,99', priceId: STRIPE_PRICE_ID_ONETIME_LARGE },
  ];

  // Obtenir le pack de crédits sélectionné
  const getSelectedCreditOption = () => {
    return creditOptions.find(option => option.id === selectedCredits);
  };

  const handleOneTimePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const creditOption = getSelectedCreditOption();
      
      const response = await api.post(`/checkout/one-time/${creditOption.priceId}`, {});
      
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Error initiating one-time checkout:', err);
      setError("Une erreur est survenue lors de l'initialisation du paiement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionPurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/checkout/subscription/${STRIPE_PRICE_ID_SUBSCRIPTION}`, {});
      
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Error initiating subscription checkout:', err);
      setError("Une erreur est survenue lors de l'initialisation de l'abonnement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pricing-page-container">
      <Navbar />
      
      <div className="pricing-page">
        <div className="pricing-header">
          <h1>Simple pricing, exceptional results</h1>
          <p>Transform your photos into artistic avatars in seconds</p>
        </div>

        <div className="pricing-container">
          {/* Carte pour le paiement ponctuel */}
          <div className="pricing-card">
            <div className="pricing-card-header">
              <h2>One-time Purchase</h2>
              <div className="pricing-price">
                <span className="currency">€</span>
                <span className="amount">{getSelectedCreditOption().price.split(',')[0]}</span>
                <span className="period">,{getSelectedCreditOption().price.split(',')[1]}</span>
              </div>
              <p className="pricing-description">One-time payment, credits usable for life</p>
            </div>
            
            <div className="pricing-features">
              <div className="credit-selector">
                <label htmlFor="credit-pack">Choose your pack:</label>
                <select 
                  id="credit-pack" 
                  value={selectedCredits}
                  onChange={(e) => setSelectedCredits(e.target.value)}
                >
                  {creditOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name} - {option.credits} credits - €{option.price}
                    </option>
                  ))}
                </select>
              </div>
              <ul>
                <li>✓ {getSelectedCreditOption().credits} credits usable on all styles</li>
                <li>✓ HD resolution for printing</li>
                <li>✓ No subscription, one-time purchase</li>
                <li>✓ Personalized gallery</li>
                <li>✓ Email support</li>
              </ul>
            </div>
            
            <div className="pricing-action">
              <button 
                className="btn btn-primary" 
                onClick={handleOneTimePurchase}
                disabled={loading}
              >
                {loading ? 'Loading...' : isAuthenticated ? 'Buy Now' : 'Log in to Buy'}
              </button>
            </div>
          </div>

          {/* Carte pour l'abonnement */}
          <div className="pricing-card featured">
            <div className="featured-badge">Popular</div>
            <div className="pricing-card-header">
              <h2>Premium Subscription</h2>
              <div className="pricing-price">
                <span className="currency">€</span>
                <span className="amount">14</span>
                <span className="period">,99</span>
                <span className="billing-period">/month</span>
              </div>
              <p className="pricing-description">Unlimited credits, cancel anytime</p>
            </div>
            
            <div className="pricing-features">
              <ul>
                <li>✓ Unlimited credits each month</li>
                <li>✓ Priority access to new styles</li>
                <li>✓ HD resolution for printing</li>
                <li>✓ Unlimited personalized gallery</li>
                <li>✓ Priority customer support</li>
                <li>✓ Cancel anytime</li>
              </ul>
            </div>
            
            <div className="pricing-action">
              <button 
                className="btn btn-featured" 
                onClick={handleSubscriptionPurchase}
                disabled={loading}
              >
                {loading ? 'Loading...' : isAuthenticated ? 'Subscribe Now' : 'Log in to Subscribe'}
              </button>
            </div>
          </div>
        </div>

        {error && <p className="pricing-error">{error}</p>}

        <div className="pricing-faq">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-item">
            <h3>What is the difference between a one-time purchase and a subscription?</h3>
            <p>A one-time purchase gives you a fixed number of credits usable without time limit. The subscription gives you access to unlimited credits each month as long as your subscription is active.</p>
          </div>
          
          <div className="faq-item">
            <h3>Can I cancel my subscription?</h3>
            <p>Yes, you can cancel your subscription at any time from your account. You will continue to enjoy your subscription until the end of the current period.</p>
          </div>
          
          <div className="faq-item">
            <h3>Are there any hidden fees?</h3>
            <p>Absolutely not. The displayed price is the only one you will pay, with no additional fees or surprises.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage; 